import { TriangleAlert } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createDocumentSelection } from '@/components/case-partner/hooks/useDocumentSelections';
import { useMatterJurisdiction } from '@/components/contexts/MatterJurisdictionContext';
import type { DocumentFilterToggle, DocumentStatusIcon } from '@/components/document-selector/DocumentSelector';
import { DocumentSelector } from '@/components/document-selector/DocumentSelector';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { Document } from '@/hooks/useDocuments';

interface BundleDocumentSelectorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matterGuid: string;
  alreadyInBundleGuids: string[];
  onAddDocuments: (docs: Document[]) => void;
  title?: string;
}

export const BundleDocumentSelectorDialog: React.FC<BundleDocumentSelectorDialogProps> = ({
  open,
  onOpenChange,
  matterGuid,
  alreadyInBundleGuids,
  onAddDocuments,
  title = 'Add Documents',
}) => {
  const { ui_labels } = useMatterJurisdiction().jurisdiction;
  const [selectedDocs, setSelectedDocs] = useState<Map<string, Document>>(new Map());

  const documentSelections = useMemo(
    () => new Map(Array.from(selectedDocs.entries()).map(([guid, doc]) => [guid, createDocumentSelection(doc)])),
    [selectedDocs]
  );

  const alreadyInBundleIdSet = useMemo(() => new Set(alreadyInBundleGuids), [alreadyInBundleGuids]);

  const getDocumentStatusIcons = useCallback((doc: Document): DocumentStatusIcon[] => {
    if (!alreadyInBundleIdSet.has(doc.id)) return [];
    return [{ Icon: TriangleAlert, className: 'text-amber-500', tooltip: ui_labels.bundle_already_in }];
  }, [alreadyInBundleIdSet, ui_labels.bundle_already_in]);

  const filterToggles = useMemo<DocumentFilterToggle[]>(() => [
    {
      id: 'hide-in-bundle',
      label: 'Hide already included',
      defaultValue: false,
      predicate: (doc: Document) => !alreadyInBundleIdSet.has(doc.id),
    },
  ], [alreadyInBundleIdSet]);

  // Reset selections when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedDocs(new Map());
    }
  }, [open]);

  const toggleDocumentSelection = useCallback((doc: Document) => {
    setSelectedDocs(prev => {
      const newMap = new Map(prev);
      if (newMap.has(doc.guid)) {
        newMap.delete(doc.guid);
      } else {
        newMap.set(doc.guid, doc);
      }
      return newMap;
    });
  }, []);

  const clearSelections = useCallback(() => {
    setSelectedDocs(new Map());
  }, []);

  const handleDone = useCallback(() => {
    const docs = Array.from(selectedDocs.values());
    if (docs.length > 0) {
      onAddDocuments(docs);
    }
    onOpenChange(false);
  }, [selectedDocs, onAddDocuments, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] w-[95vw] max-h-[90vh] h-[90vh] p-0 gap-0 flex flex-col">
        <DialogHeader className="px-6 py-4 border-b border-gray-200 shrink-0">
          <DialogTitle className="text-lg font-semibold">{title}</DialogTitle>
          <DialogDescription className="text-sm text-gray-500 mt-1">
            Browse folders and select documents to add.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-hidden">
          <DocumentSelector
            matterSlug={matterGuid}
            documentSelections={documentSelections}
            onToggleSelection={toggleDocumentSelection}
            onClearSelection={clearSelections}
            showUploadButton={false}
            onDone={handleDone}
            getDocumentStatusIcons={getDocumentStatusIcons}
            filterToggles={filterToggles}
            className="h-full"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
